import { useEffect, useState } from "react";
import { countryFromTrace, currencyForCountry, detectCurrency, type Currency } from "./currency";

const COUNTRY_KEY = "om.country";
let pending: Promise<string | null> | null = null;

/**
 * The visitor's country as Cloudflare's edge sees it (same-origin
 * /cdn-cgi/trace, no third party). Fetched once per tab session. Resolves
 * null off Cloudflare (localhost) or on any failure.
 */
function visitorCountry(): Promise<string | null> {
  try {
    const cached = sessionStorage.getItem(COUNTRY_KEY);
    if (cached) return Promise.resolve(cached);
  } catch {
    /* storage blocked */
  }
  if (!pending) {
    pending = fetch("/cdn-cgi/trace", {
      cache: "no-store",
      signal: typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(3000) : undefined,
    })
      .then((r) => (r.ok ? r.text() : ""))
      .then((body) => {
        const country = countryFromTrace(body);
        if (country) {
          try {
            sessionStorage.setItem(COUNTRY_KEY, country);
          } catch {
            /* ignore */
          }
        }
        return country;
      })
      .catch(() => null);
  }
  return pending;
}

/**
 * Billing currency for this visitor: CAD in Canada, USD everywhere else.
 * Starts from the timezone guess (instant, no flash for most people), then
 * settles on the real country from the edge, so an en-CA laptop still on
 * Toronto time in Tashkent is correctly shown USD.
 *
 * `prerendered`: the component is hydrated from the build-time landing HTML,
 * which always says USD. The first render must match it, so the timezone guess
 * is applied right after mount instead (long before the pricing section is
 * scrolled into view).
 */
export function useCurrency(prerendered = false): Currency {
  const [currency, setCurrency] = useState<Currency>(() => (prerendered ? "usd" : detectCurrency()));
  useEffect(() => {
    setCurrency(detectCurrency());
    let live = true;
    void visitorCountry().then((country) => {
      if (live && country) setCurrency(currencyForCountry(country));
    });
    return () => {
      live = false;
    };
  }, []);
  return currency;
}
